import { ORDER } from '../core/systems.js';
import { MOVESETS, MOVESET_NAMES } from '../combat/moveset.js';
import { createProgressHud } from './progresshud.js';

/**
 * ===========================================================================
 *  PROGRESS — rank, weapon proficiency, and the first number that climbs
 * ===========================================================================
 *
 * DEV_PLAN Step 2. Step 1 gave the game a record that outlives a battle; this
 * is what actually writes to it. Until now `progressionLegibility` scored 0 by
 * definition — nothing was gained, so there was nothing to display.
 *
 * THE SHAPE IS ORIGINS', NOT DW8'S. Every weapon earns from every battle, the
 * equipped one earns most. A player who spends a battle experimenting with a
 * set they are bad at is slowed, never punished, and never has to grind a
 * weapon in isolation to make it usable. That is one constant (`MAIN_SHARE`)
 * and it is the single most important number in the file.
 *
 * RANK IS DERIVED, NEVER STORED AS TRUTH. `rank = 1 + Σ proficiency levels`,
 * recomputed from the proficiency table every time it is read. A stored rank
 * and a stored proficiency table are two sources of truth for one fact, and
 * the first save migration that touched either would put them out of step. The
 * save still carries `rank` because the schema promises it and the UI reads it
 * without the moveset table to hand — but `rankOf()` owns its value and
 * overwrites it on every commit.
 *
 * WHAT IT DOES NOT DO. Nothing here changes a hitbox, a damage number or a
 * hit point. Rank and proficiency are legible and they persist; they do not
 * yet make the player stronger, and the titles below are what the ladder
 * grants today. Hanging a stat multiplier off it would mean editing combat or
 * player state — modules this one does not own (docs/CONTRACTS.md §0) — and
 * would rebalance a game whose combat has been judged at its current numbers.
 * The table is the mechanism; Steps 3, 8 and 10 are what hang weight on it.
 */

/* --------------------------------------------------------------- the model */

/**
 * What a battle pays. These are the tuning surface, and they were measured
 * rather than guessed: see `critic_progress` and the Step 2 verdict in
 * PROGRESS.jsonl for the battle totals these produce.
 */
export const AWARD = {
  kill: 10,          // one grunt
  officer: 250,      // a named officer — worth twenty-five men, and it should be
  base: 120,         // a base passing into our hands
  objective: 180,    // an objective completed
  perSecond: 1,      // field time, so a long defensive battle is not worth less
};

/** The equipped weapon's share. The rest is split evenly among the others. */
export const MAIN_SHARE = 0.70;

/**
 * Level k costs `XP_STEP * k`, so reaching it costs `XP_STEP * k(k+1)/2`
 * cumulative: 400, 1200, 2400, 4000, 6000, ...
 *
 * Triangular rather than exponential on purpose. An exponential curve makes
 * level 2 feel like level 1 and level 9 feel impossible, which is the shape
 * that turns a progression system into a grind; this one keeps every level
 * roughly one more battle than the last for the whole of the band a player
 * will actually see.
 */
export const XP_STEP = 400;
export const LEVEL_CAP = 20;

/** Cumulative XP to have REACHED level k. `xpForLevel(0)` is 0. */
export function xpForLevel(k) {
  const n = k < 0 ? 0 : k > LEVEL_CAP ? LEVEL_CAP : k | 0;
  return XP_STEP * n * (n + 1) / 2;
}

/** The level a given XP total buys. Inverse of `xpForLevel`, clamped. */
export function levelFor(xp) {
  const x = xp > 0 ? xp : 0;
  // k(k+1)/2 <= x/XP_STEP  =>  k = floor((sqrt(8x/XP_STEP + 1) - 1) / 2)
  const k = Math.floor((Math.sqrt(8 * x / XP_STEP + 1) - 1) / 2);
  return k < 0 ? 0 : k > LEVEL_CAP ? LEVEL_CAP : k;
}

/** Rank is the sum of what every weapon has learned, plus one. */
export function rankOf(proficiency) {
  let sum = 0;
  for (let i = 0; i < MOVESET_NAMES.length; i++) {
    sum += levelFor((proficiency && proficiency[MOVESET_NAMES[i]]) || 0);
  }
  return 1 + sum;
}

/**
 * The ladder, and what each rung is called.
 *
 * Majapahit-era service ranks, which is the setting `src/core/theme.js` and
 * the roster already establish (Wilwatikta, Bhayangkara, Senapati). They are
 * TITLES and nothing else — earned, shown on the results screen and the
 * briefing, and carried in the save. Naming a grant the ladder does not make
 * would be worse than naming a modest one honestly.
 */
/**
 * WHAT THE LADDER GRANTS, beyond a title.
 *
 * The Step 2 verdict and every re-score since recorded the same ceiling on
 * `reasonToReplay`: "no unlock changes what the player can DO. No weapon,
 * hero, stage or mode is gated behind the ladder, so what compounds is a pool
 * size and a title." It was held at 4 for that reason across three rounds.
 *
 * A weapon is the cheapest honest thing to gate, because the game now has four
 * and the fourth is genuinely a different instrument rather than a reskin —
 * tools/weapon-distinct.mjs puts the gada 1.50 m from the glaive, the widest
 * gap in the matrix. So: the two weapons the story starts you with are free,
 * the third is a few battles in, and the gada waits at Bhayangkara.
 *
 * Deliberately NOT gated: the first two weapons and every stage. A ladder that
 * withholds the opening hour is a worse game, not a deeper one.
 */
export const GRANTS = [
  { rank: 2, id: 'weapon:twinSabres', kind: 'weapon', ref: 'twinSabres', name: 'Golok Kembar',
    note: 'Paired sabres — twice the cycle rate, half the reach' },
  { rank: 4, id: 'weapon:gada', kind: 'weapon', ref: 'gada', name: 'Gada Rujakpala',
    note: 'The mace — swings under a guard, and breaks the ones it cannot' },
  { rank: 7, id: 'weapon:cambuk', kind: 'weapon', ref: 'cambuk', name: 'Cambuk Naga',
    note: 'The chain — owns four metres, and nothing inside one' },
  /* Granted last, and deliberately after the cambuk: the two are inverses, so
   * the ladder hands the player the weapon that owns the rim and then the one
   * that owns the doorway. */
  { rank: 10, id: 'weapon:kapak', kind: 'weapon', ref: 'kapak', name: 'Kapak Garuda',
    note: 'Paired axes — the shortest reach in the game, and the fastest hands' },
];

/** Everything the ladder has granted at this rank, earliest first. */
export function grantsFor(rank) { return GRANTS.filter((g) => rank >= g.rank); }
/** The next thing the ladder will grant, or null at the top. */
export function nextGrant(rank) { return GRANTS.find((g) => rank < g.rank) || null; }
/** Is this moveset available at this rank? Ungated weapons are always true. */
export function weaponUnlocked(setName, rank) {
  const g = GRANTS.find((x) => x.kind === 'weapon' && x.ref === setName);
  return !g || rank >= g.rank;
}

export const TITLES = [
  { rank: 1, id: 'cantrik', name: 'Cantrik', note: 'Sworn to the banner' },
  { rank: 2, id: 'prajurit', name: 'Prajurit', note: 'Blooded on the field' },
  { rank: 4, id: 'bhayangkara', name: 'Bhayangkara', note: 'Of the guard corps' },
  { rank: 6, id: 'lurah', name: 'Lurah', note: 'Trusted with a company' },
  { rank: 9, id: 'tumenggung', name: 'Tumenggung', note: 'Commands a wing' },
  { rank: 12, id: 'rakryan', name: 'Rakryan', note: 'Named in the court' },
  { rank: 16, id: 'senapati', name: 'Senapati', note: 'Given the whole line' },
  { rank: 20, id: 'mahapatih', name: 'Mahapatih', note: 'Second to no one living' },
];

/** The highest title this rank has earned. Never null — rank 1 has one. */
export function titleFor(rank) {
  let out = TITLES[0];
  for (let i = 0; i < TITLES.length; i++) if (rank >= TITLES[i].rank) out = TITLES[i];
  return out;
}

/** The next title above this rank, or null at the top of the ladder. */
export function nextTitle(rank) {
  for (let i = 0; i < TITLES.length; i++) if (TITLES[i].rank > rank) return TITLES[i];
  return null;
}

/* -------------------------------------------------------------- the system */

/** A zeroed proficiency table with every known weapon present. */
function emptyProficiency() {
  const out = {};
  for (let i = 0; i < MOVESET_NAMES.length; i++) out[MOVESET_NAMES[i]] = 0;
  return out;
}

export function createProgress() {
  /* `base` is what the save held when this battle began; `earned` is what this
   * battle has added. They stay separate all battle so the HUD can show what
   * THIS fight is worth, and so a defeat can be committed on the same path as
   * a victory without double-counting a re-fight. */
  let base = emptyProficiency();
  let earned = emptyProficiency();
  let session = { xp: 0, kills: 0, officers: 0, bases: 0, objectives: 0, seconds: 0 };
  let W = null, pollT = 0, heldWas = -1, doneWas = -1, startRank = 1;
  let startLevel = 0, startFrac = 0, liveRank = 1;

  /** The equipped weapon, or the first one if the player is not up yet. */
  function equipped() {
    const n = W && W.player && W.player.weapon;
    return (n && MOVESETS[n]) ? n : MOVESET_NAMES[0];
  }

  /**
   * Pay `xp` into the table: the equipped weapon takes MAIN_SHARE, the rest
   * split the remainder evenly. Fractions are kept — rounding each award to an
   * integer would silently drop most of a grunt's share on a three-weapon
   * roster, and the bar is read, not counted.
   */
  function award(xp) {
    if (!(xp > 0)) return;
    const main = equipped();
    const others = MOVESET_NAMES.length - 1;
    const rest = others > 0 ? (xp * (1 - MAIN_SHARE)) / others : 0;
    for (let i = 0; i < MOVESET_NAMES.length; i++) {
      const n = MOVESET_NAMES[i];
      earned[n] += (n === main) ? xp * MAIN_SHARE : rest;
    }
    session.xp += xp;

    /* Announce a rank change the moment it happens. Other systems hang real
     * behaviour off rank now — player.js widens the bravery cap on it — and
     * polling a derived number every tick to find out would be the wrong shape.
     *
     * Recomputed rather than threshold-armed on purpose: every weapon earns a
     * share, so ANY of them can be the one that levels and moves the sum. The
     * recompute is three square roots and allocates nothing, which is well
     * inside what CONTRACTS.md section 5 is actually about. */
    let sum = 0;
    for (let i = 0; i < MOVESET_NAMES.length; i++) sum += levelFor(totalOf(MOVESET_NAMES[i]));
    const r = 1 + sum;
    if (r !== liveRank) { liveRank = r; W?.bus?.emit?.('progress:rank', r); }
  }

  /** Total XP on one weapon, save plus this battle. */
  function totalOf(name) { return (base[name] || 0) + (earned[name] || 0); }

  function snapshotLevels() {
    const out = {};
    for (let i = 0; i < MOVESET_NAMES.length; i++) {
      const n = MOVESET_NAMES[i];
      out[n] = levelFor(totalOf(n));
    }
    return out;
  }

  function snapshotProficiency() {
    const out = {};
    for (let i = 0; i < MOVESET_NAMES.length; i++) {
      const n = MOVESET_NAMES[i];
      out[n] = Math.round(totalOf(n));
    }
    return out;
  }

  /** Re-read the save. Called at init and at the start of every battle. */
  function reload(w) {
    const saved = w.save && w.save.get ? w.save.get('proficiency') : null;
    base = emptyProficiency();
    if (saved) {
      for (let i = 0; i < MOVESET_NAMES.length; i++) {
        const n = MOVESET_NAMES[i];
        if (Number.isFinite(saved[n])) base[n] = saved[n];
      }
    }
    earned = emptyProficiency();
    session = { xp: 0, kills: 0, officers: 0, bases: 0, objectives: 0, seconds: 0 };
    heldWas = -1; doneWas = -1; pollT = 0;
    startRank = rankOf(base);
    liveRank = startRank;
    // Where the equipped weapon's bar stood at the first tick of this battle.
    // Captured here rather than derived later because `earned` is what moves
    // it, and after the battle there is no way back to the starting point.
    const m0 = equipped();
    const l0 = levelFor(base[m0] || 0);
    const f0 = xpForLevel(l0), s0 = xpForLevel(l0 + 1) - f0;
    startLevel = l0;
    startFrac = s0 > 0 ? ((base[m0] || 0) - f0) / s0 : 0;
  }

  /** Write this battle into the record. Idempotent per battle by `reload()`. */
  function commit(w) {
    const prof = snapshotProficiency();
    const rank = rankOf(prof);
    w.save?.set?.('proficiency', prof);
    w.save?.set?.('rank', rank);
    // A title is granted once and kept. `unlock` already dedupes.
    const t = titleFor(rank);
    if (t) w.save?.unlock?.('title:' + t.id);
    /* ...and anything the ladder grants at this rank. `unlock` dedupes, so
     * this is idempotent and a re-commit grants nothing twice. The event is
     * what the HUD hangs a toast off; the save is what makes it carry. */
    const granted = [];
    for (const g of grantsFor(rank)) {
      if (w.save?.unlock?.(g.id)) { granted.push(g); w.bus?.emit?.('progress:grant', g); }
    }
    return { prof, rank, granted };
  }

  /* The HUD reads this EVERY RENDERED FRAME, so it is filled in place. A fresh
   * object per frame is exactly the allocation docs/CONTRACTS.md ss5 bans, and
   * at 60 Hz it is the difference between a HUD that costs nothing and one
   * that feeds the collector. `info()` copies out of it for everyone else. */
  const _live = {
    rank: 1, rankAtStart: 1, title: null, next: null,
    weapon: '', weaponLabel: '', level: 0, levelXp: 0, levelSpan: 0,
    levelFrac: 0, totalXp: 0, earnedXp: 0,
  };

  function refresh() {
    const main = equipped();
    const total = totalOf(main);
    const lvl = levelFor(total);
    const floor = xpForLevel(lvl), span = xpForLevel(lvl + 1) - floor;
    let sum = 0;
    for (let i = 0; i < MOVESET_NAMES.length; i++) sum += levelFor(totalOf(MOVESET_NAMES[i]));
    _live.rank = 1 + sum;
    _live.rankAtStart = startRank;
    _live.title = titleFor(_live.rank);
    _live.next = nextTitle(_live.rank);
    _live.weapon = main;
    _live.weaponLabel = (MOVESETS[main] && MOVESETS[main].label) || main;
    _live.level = lvl;
    _live.levelXp = Math.round(total - floor);
    _live.levelSpan = Math.round(span);
    // 1.0 at the cap, where there is no next level to fill toward
    _live.levelFrac = lvl >= LEVEL_CAP ? 1 : (span > 0 ? (total - floor) / span : 0);
    _live.totalXp = Math.round(total);
    _live.earnedXp = Math.round(session.xp);
    return _live;
  }

  const api = {
    /** The live career rank, allocation-free. Read by player.js every reset. */
    get rank() { return liveRank; },

    /** Everything the results screen and the probes read. Allocates; rare. */
    info() {
      const L = refresh();
      return {
        rank: L.rank, rankAtStart: L.rankAtStart, title: L.title, next: L.next,
        levelAtStart: startLevel, levelFracAtStart: startFrac,
        weapon: L.weapon, weaponLabel: L.weaponLabel,
        level: L.level, levelXp: L.levelXp, levelSpan: L.levelSpan,
        levelFrac: L.levelFrac, totalXp: L.totalXp, earnedXp: L.earnedXp,
        proficiency: snapshotProficiency(),
        levels: snapshotLevels(),
        session: { ...session, seconds: +session.seconds.toFixed(1) },
      };
    },

    /* The ladder's grants. `locked(set)` is the question the front end and the
     * player both actually ask: may I pick this up yet? */
    grants: GRANTS,
    granted() { return grantsFor(liveRank); },
    nextGrant() { return nextGrant(liveRank); },
    unlocked(setName) { return weaponUnlocked(setName, liveRank); },

    /** Exposed so a contract test can exercise the award path directly. */
    award,
    commit: () => commit(W),
    reload: () => reload(W),

    /** Wipe every total, save and battle alike. Tests and a UI "erase". */
    reset() {
      base = emptyProficiency();
      earned = emptyProficiency();
      session = { xp: 0, kills: 0, officers: 0, bases: 0, objectives: 0, seconds: 0 };
      heldWas = -1; doneWas = -1; startRank = 1;
      startLevel = 0; startFrac = 0; liveRank = 1;
      return api.info();
    },

    /* The pure model, reachable from a running world. The curve and the ladder
     * are arithmetic, not behaviour: a trace cannot see an off-by-one at a
     * level boundary, so the contract test checks them directly and needs them
     * without importing this module into a page it cannot import into. */
    model: {
      xpForLevel, levelFor, rankOf, titleFor, nextTitle,
      TITLES, AWARD, MAIN_SHARE, XP_STEP, LEVEL_CAP,
    },
  };

  const hud = createProgressHud();

  return {
    name: 'progress', order: ORDER.MISSION,

    init(w) {
      W = w;
      reload(w);
      w.progress = api;

      /* Every award hangs off an event that already exists. combat.js emits
       * `kill` with the killer's id and increments w.player.kills on exactly
       * the same condition, so these two counts must agree — the probe asserts
       * it rather than trusting it. */
      w.bus.on('kill', (victim, src) => {
        if (src !== w.playerId) return;
        session.kills++;
        award(AWARD.kill);
      });

      /* An officer also emits a plain `kill`, so this is the BONUS on top of
       * the grunt award rather than the whole of an officer's worth. */
      w.bus.on('officer:down', (id, src) => {
        if (src !== w.playerId) return;
        session.officers++;
        award(AWARD.officer);
      });

      w.bus.on('mission:start', () => reload(w));
      w.bus.on('mission:end', () => commit(w));
    },

    fixedUpdate(w, dt) {
      const m = w.mission;
      if (!m || m.state !== 'active' || m.paused) return;

      session.seconds += dt;
      award(AWARD.perSecond * dt);

      /* Bases and objectives have no events, so they are polled — at 4 Hz, not
       * per frame: `mission.info()` builds three arrays per call and this is a
       * fixedUpdate. `held` is cheap enough to read on the same tick. */
      pollT -= dt;
      if (pollT > 0) return;
      pollT = 0.25;

      const held = m.held ? m.held(0) : 0;
      if (heldWas < 0) heldWas = held;
      else if (held > heldWas) { session.bases += (held - heldWas); award(AWARD.base * (held - heldWas)); }
      // a base lost is not a debt; it is simply not paid for again
      if (held !== heldWas) heldWas = held;

      const info = m.info ? m.info() : null;
      if (info && info.objectives) {
        let done = 0;
        for (let i = 0; i < info.objectives.length; i++) if (info.objectives[i].state === 'done') done++;
        if (doneWas < 0) doneWas = done;
        else if (done > doneWas) { session.objectives += (done - doneWas); award(AWARD.objective * (done - doneWas)); }
        if (done !== doneWas) doneWas = done;
      }
    },

    /** Presentation only: the rank strip, and the between-battles band. */
    update(w, dt, alpha, realDt) {
      if (!hud.mounted && !hud.mount(w)) return;
      const m = w.mission;
      // never over a modal, and never on the front end — same test mission.js
      // applies to its own plaque, for the same reason: the menus layer was
      // appended to the HUD root before ours and an open scrim sits UNDER us.
      const uiState = w.ui ? w.ui.state : 'playing';
      const live = refresh();
      const show = !!m && m.state === 'active' && !m.paused && uiState === 'playing';
      hud.frame(w, realDt, show, live);
      // The band is the opposite: it belongs ON the two screens that bracket a
      // battle and nowhere else. `results` is where the gain is read, and
      // `briefing` is where the next rung is named BEFORE the fight — which is
      // the half of `progressionLegibility` a results screen cannot answer.
      hud.bandFrame(w, realDt, uiState, live, startFrac);
    },

    dispose() { hud.dispose(); },
  };
}
